/**
 * Invoices Page
 * Enhanced with bulk actions
 */

import React, { useState, useMemo } from 'react';
import { useInvoiceStore } from '../store/invoiceStore';
import { useAuthStore } from '../store/authStore';
import { useClientStore } from '../store/clientStore';
import { useDataSubscriptions } from '../hooks/useDataSubscriptions';
import { formatCurrency, formatDate, isOverdue, daysDifference } from '../utils/formatters';
import { downloadInvoicePDF, previewInvoicePDF } from '../utils/pdfGenerator';
import { getUserProfile } from '../services/userProfileService';
import {
  validateBulkSelection,
  filterDeletableInvoices,
  prepareBulkExportData,
  generateCsvContent,
  aggregateBulkResults,
  getBulkActionSummary,
} from '../services/bulkActionService';
import CreateInvoiceModal from '../components/CreateInvoiceModal';
import RecordPaymentModal from '../components/RecordPaymentModal';
import InvoicePreviewModal from '../components/InvoicePreviewModal';
import './Invoices.css';

const Invoices = () => {
  useDataSubscriptions();

  const user = useAuthStore((state) => state.user);
  const invoices = useInvoiceStore((state) => state.invoices);
  const clients = useClientStore((state) => state.clients);
  const sendReminder = useInvoiceStore((state) => state.sendReminder);
  const deleteInvoice = useInvoiceStore((state) => state.deleteInvoice);
  const voidInvoice = useInvoiceStore((state) => state.voidInvoice);
  const updateInvoice = useInvoiceStore((state) => state.updateInvoice);
  const sendInvoice = useInvoiceStore((state) => state.sendInvoice);
  const [showModal, setShowModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, paid, overdue, void
  const [userProfile, setUserProfile] = useState(null);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Load user profile for PDF generation
  React.useEffect(() => {
    if (user) {
      getUserProfile(user.uid).then(setUserProfile);
    }
  }, [user]);

  const getStatusBadge = (invoice) => {
    if (invoice.status === 'paid') {
      return <span className="badge badge-success">Paid</span>;
    }
    if (invoice.status === 'void') {
      return <span className="badge badge-secondary">Void</span>;
    }
    if (invoice.status === 'partial') {
      return <span className="badge badge-info">Partial</span>;
    }
    if (invoice.status === 'draft') {
      return <span className="badge badge-info">Draft</span>;
    }
    if (isOverdue(invoice.dueDate)) {
      return <span className="badge badge-danger">Overdue</span>;
    }
    return <span className="badge badge-warning">Pending</span>;
  };

  const handleVoidInvoice = async (invoice) => {
    const reason = window.prompt('Reason for voiding this invoice (optional):');
    if (reason === null) return; // User cancelled

    if (!window.confirm(`Void invoice ${invoice.invoiceNumber}? This cannot be undone.`)) {
      return;
    }

    try {
      await voidInvoice(invoice.id, reason);
      alert('Invoice voided successfully');
    } catch (error) {
      alert('Error voiding invoice: ' + error.message);
    }
  };

  const handleChangeCurrency = async (invoice) => {
    const currencies = ['USD', 'PHP', 'SGD', 'EUR', 'GBP', 'AUD'];
    const currentCurrency = invoice.currency || 'USD';
    const newCurrency = window.prompt(
      `Change currency for ${invoice.invoiceNumber}\nCurrent: ${currentCurrency}\nEnter new currency (${currencies.join(', ')}):`,
      currentCurrency
    );

    if (!newCurrency || newCurrency === currentCurrency) return;

    if (!currencies.includes(newCurrency.toUpperCase())) {
      alert(`Invalid currency. Please use one of: ${currencies.join(', ')}`);
      return;
    }

    try {
      // Pass isAdmin=true for non-draft invoices
      const isAdmin = user?.email?.endsWith('@fusionict.com');
      await updateInvoice(invoice.id, { currency: newCurrency.toUpperCase() }, isAdmin);
      alert(`Currency changed to ${newCurrency.toUpperCase()}`);
    } catch (error) {
      alert('Error changing currency: ' + error.message);
    }
  };

  const handleEditProjectName = async (invoice) => {
    const currentName = invoice.projectName || invoice.clientName || '';
    const newName = window.prompt(
      `Edit project name for ${invoice.invoiceNumber}:`,
      currentName
    );

    if (!newName || newName === currentName) return;

    try {
      const isAdmin = user?.email?.endsWith('@fusionict.com');
      await updateInvoice(invoice.id, { projectName: newName }, isAdmin);
      alert(`Project name updated to "${newName}"`);
    } catch (error) {
      alert('Error updating project name: ' + error.message);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      downloadInvoicePDF(invoice, userProfile || {});
    } catch (error) {
      alert('Error generating PDF: ' + error.message);
    }
  };

  const handlePreviewPDF = async (invoice) => {
    try {
      previewInvoicePDF(invoice, userProfile || {});
    } catch (error) {
      alert('Error previewing PDF: ' + error.message);
    }
  };

  const handleRecordPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleShowSendPreview = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPreviewModal(true);
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await sendInvoice(invoiceId);
      alert('Invoice sent successfully!');
      setShowPreviewModal(false);
      setSelectedInvoice(null);
    } catch (error) {
      throw error; // Let the modal handle the error display
    }
  };

  const handleSendReminder = async (invoiceId) => {
    if (!window.confirm('Send payment reminder for this invoice?')) {
      return;
    }

    try {
      await sendReminder(invoiceId);
      alert('Payment reminder sent successfully!');
    } catch (error) {
      alert('Error sending reminder: ' + error.message);
    }
  };

  // Filter invoices
  const filteredInvoices = invoices.filter(invoice => {
    if (filter === 'pending') return invoice.status === 'pending';
    if (filter === 'paid') return invoice.status === 'paid';
    if (filter === 'overdue') return invoice.status === 'pending' && isOverdue(invoice.dueDate);
    if (filter === 'void') return invoice.status === 'void';
    if (filter === 'draft') return invoice.status === 'draft';
    return true;
  });

  // Get selected invoices
  const selectedInvoices = useMemo(() => {
    return filteredInvoices.filter((inv) => selectedIds.has(inv.id));
  }, [filteredInvoices, selectedIds]);

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredInvoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredInvoices.map((inv) => inv.id)));
    }
  };

  const toggleSelect = (invoiceId) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(invoiceId)) {
      newSelected.delete(invoiceId);
    } else {
      newSelected.add(invoiceId);
    }
    setSelectedIds(newSelected);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Bulk actions
  const handleBulkExport = () => {
    const validation = validateBulkSelection(Array.from(selectedIds));
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    const exportData = prepareBulkExportData(selectedInvoices);
    const csvContent = generateCsvContent(exportData);

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `invoices-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    clearSelection();
  };

  const handleBulkMarkSent = async () => {
    const validation = validateBulkSelection(Array.from(selectedIds));
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    const draftInvoices = selectedInvoices.filter((inv) => inv.status === 'draft' || inv.status === 'pending');
    if (draftInvoices.length === 0) {
      alert('No draft/pending invoices selected');
      return;
    }

    if (!window.confirm(`Mark ${draftInvoices.length} invoice(s) as sent?`)) {
      return;
    }

    setBulkProcessing(true);
    const results = [];

    for (const invoice of draftInvoices) {
      try {
        await updateInvoice(invoice.id, { status: 'sent', sentAt: new Date() });
        results.push({ success: true, invoiceId: invoice.id });
      } catch (error) {
        results.push({ success: false, invoiceId: invoice.id, error: error.message });
      }
    }

    const summary = aggregateBulkResults(results);
    alert(`Marked ${summary.successCount} invoice(s) as sent. ${summary.failureCount} failed.`);
    setBulkProcessing(false);
    clearSelection();
  };

  const handleBulkDelete = async () => {
    const validation = validateBulkSelection(Array.from(selectedIds));
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    const { deletable, skipped } = filterDeletableInvoices(selectedInvoices);

    if (deletable.length === 0) {
      alert('Only draft invoices can be deleted. No draft invoices selected.');
      return;
    }

    const confirmMsg = skipped.length > 0
      ? `Delete ${deletable.length} draft invoice(s)? (${skipped.length} non-draft invoices will be skipped)`
      : `Delete ${deletable.length} draft invoice(s)?`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setBulkProcessing(true);
    const results = [];

    for (const invoice of deletable) {
      try {
        await deleteInvoice(invoice.id);
        results.push({ success: true, invoiceId: invoice.id });
      } catch (error) {
        results.push({ success: false, invoiceId: invoice.id, error: error.message });
      }
    }

    const summary = aggregateBulkResults(results);
    alert(`Deleted ${summary.successCount} invoice(s). ${summary.failureCount} failed.`);
    setBulkProcessing(false);
    clearSelection();
  };

  const handleBulkDownloadPDF = async () => {
    const validation = validateBulkSelection(Array.from(selectedIds));
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    if (selectedInvoices.length > 10) {
      if (!window.confirm(`Download ${selectedInvoices.length} PDFs? This may take a moment.`)) {
        return;
      }
    }

    setBulkProcessing(true);

    for (const invoice of selectedInvoices) {
      try {
        downloadInvoicePDF(invoice, userProfile || {});
        // Small delay between downloads to prevent browser blocking
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {
        console.error(`Error downloading ${invoice.invoiceNumber}:`, error);
      }
    }

    setBulkProcessing(false);
    clearSelection();
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Invoices</h1>
          <p className="page-subtitle">
            Generate and manage your invoices
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          + Generate Invoice
        </button>
      </div>

      <div className="filter-bar">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending
        </button>
        <button
          className={`filter-btn ${filter === 'overdue' ? 'active' : ''}`}
          onClick={() => setFilter('overdue')}
        >
          Overdue
        </button>
        <button
          className={`filter-btn ${filter === 'paid' ? 'active' : ''}`}
          onClick={() => setFilter('paid')}
        >
          Paid
        </button>
        <button
          className={`filter-btn ${filter === 'draft' ? 'active' : ''}`}
          onClick={() => setFilter('draft')}
        >
          Draft
        </button>
        <button
          className={`filter-btn ${filter === 'void' ? 'active' : ''}`}
          onClick={() => setFilter('void')}
        >
          Void
        </button>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bulk-action-bar">
          <div className="bulk-info">
            <span className="bulk-count">{selectedIds.size} selected</span>
            <button className="btn-link" onClick={clearSelection}>
              Clear
            </button>
          </div>
          <div className="bulk-actions">
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleBulkExport}
              disabled={bulkProcessing}
              title="Export to CSV"
            >
              Export CSV
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleBulkDownloadPDF}
              disabled={bulkProcessing}
              title="Download PDFs"
            >
              Download PDFs
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleBulkMarkSent}
              disabled={bulkProcessing}
              title="Mark as Sent"
            >
              Mark Sent
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={handleBulkDelete}
              disabled={bulkProcessing}
              title="Delete (drafts only)"
            >
              Delete
            </button>
          </div>
          {bulkProcessing && <span className="bulk-processing">Processing...</span>}
        </div>
      )}

      {filteredInvoices.length === 0 ? (
        <div className="empty-state">
          <p>No invoices yet. Generate your first invoice from time entries!</p>
        </div>
      ) : (
        <div className="invoice-list">
          <table className="data-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === filteredInvoices.length && filteredInvoices.length > 0}
                    onChange={toggleSelectAll}
                    title="Select all"
                  />
                </th>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Issue Date</th>
                <th>Due Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(invoice => (
                <tr key={invoice.id} className={selectedIds.has(invoice.id) ? 'selected-row' : ''}>
                  <td className="checkbox-col">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(invoice.id)}
                      onChange={() => toggleSelect(invoice.id)}
                    />
                  </td>
                  <td className="font-mono">{invoice.invoiceNumber}</td>
                  <td>{invoice.clientName}</td>
                  <td>{formatDate(invoice.issueDate)}</td>
                  <td>
                    {formatDate(invoice.dueDate)}
                    {invoice.status === 'pending' && (
                      <div className="text-small">
                        {isOverdue(invoice.dueDate)
                          ? `${Math.abs(daysDifference(invoice.dueDate))} days overdue`
                          : `Due in ${daysDifference(invoice.dueDate)} days`
                        }
                      </div>
                    )}
                    {invoice.viewedAt && (
                      <div className="text-small text-success" title={`Viewed ${invoice.viewCount || 1} time(s)`}>
                        👁️ Viewed {formatDate(invoice.viewedAt)}
                      </div>
                    )}
                  </td>
                  <td className="font-bold">{formatCurrency(invoice.totalAmount, invoice.currency)}</td>
                  <td>{getStatusBadge(invoice)}</td>
                  <td>
                    <div className="action-buttons">
                      {/* Download PDF */}
                      <button
                        className="btn-sm btn-secondary"
                        onClick={() => handleDownloadPDF(invoice)}
                        title="Download PDF"
                      >
                        📥
                      </button>

                      {/* Preview PDF */}
                      <button
                        className="btn-sm btn-secondary"
                        onClick={() => handlePreviewPDF(invoice)}
                        title="Preview PDF"
                      >
                        👁️
                      </button>

                      {/* Send Invoice */}
                      {(invoice.status === 'draft' || invoice.status === 'pending') && (
                        <button
                          className="btn-sm btn-primary"
                          onClick={() => handleShowSendPreview(invoice)}
                          title="Send to client"
                        >
                          ✉️
                        </button>
                      )}

                      {/* Record Payment */}
                      {invoice.status !== 'paid' && invoice.status !== 'void' && (
                        <button
                          className="btn-sm btn-secondary"
                          onClick={() => handleRecordPayment(invoice)}
                          title="Record payment"
                        >
                          💰
                        </button>
                      )}

                      {/* Edit Project Name - for admins on any non-void invoice */}
                      {invoice.status !== 'void' && user?.email?.endsWith('@fusionict.com') && (
                        <button
                          className="btn-sm btn-secondary"
                          onClick={() => handleEditProjectName(invoice)}
                          title="Edit project name"
                        >
                          ✏️
                        </button>
                      )}

                      {/* Change Currency - for admins on any non-void invoice */}
                      {invoice.status !== 'void' && user?.email?.endsWith('@fusionict.com') && (
                        <button
                          className="btn-sm btn-secondary"
                          onClick={() => handleChangeCurrency(invoice)}
                          title="Change currency"
                        >
                          💱
                        </button>
                      )}

                      {/* Void Invoice - for non-draft, non-paid, non-void invoices */}
                      {invoice.status !== 'draft' && invoice.status !== 'paid' && invoice.status !== 'void' && (
                        <button
                          className="btn-sm btn-danger"
                          onClick={() => handleVoidInvoice(invoice)}
                          title="Void invoice"
                        >
                          🚫
                        </button>
                      )}

                      {/* Send Reminder */}
                      {invoice.status === 'pending' && (
                        <button
                          className="btn-sm btn-secondary"
                          onClick={() => handleSendReminder(invoice.id)}
                          title="Send reminder"
                        >
                          🔔
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <CreateInvoiceModal onClose={() => setShowModal(false)} />
      )}
      
      {showPaymentModal && selectedInvoice && (
        <RecordPaymentModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedInvoice(null);
          }}
          onSuccess={() => {
            // Reload data will happen automatically via subscriptions
          }}
        />
      )}

      {showPreviewModal && selectedInvoice && (
        <InvoicePreviewModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedInvoice(null);
          }}
          onSend={handleSendInvoice}
        />
      )}
    </div>
  );
};

export default Invoices;
